import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useHidDevice } from '../hid/useHidDevice.ts';
import { pushDraft } from '../features/push/push.ts';
import { markPushed } from '../features/profiles/storage.ts';
import { JigState, PatternKind } from '../protocol/constants.ts';
import type { JigStep } from '../protocol/types.ts';
import {
  defaultDraft,
  draftFromDevice,
  draftMatchesDevice,
  hasButtonActions,
  validateDraft,
  type Draft,
} from '../domain/draft.ts';

type Transport = NonNullable<ReturnType<typeof useHidDevice>['transport']>;
type PushState =
  | { kind: 'idle' }
  | { kind: 'busy'; message: string }
  | { kind: 'ok'; message: string; saved: boolean }
  | { kind: 'bad'; message: string };

interface Session {
  generation: number;
  transport: Transport;
  loaded: boolean;
  reading: boolean;
  adoptOnLoad: boolean;
}

// Re-read the getter across awaits; a successful earlier check is not a guarantee.
function isTransportClosed(transport: Transport): boolean {
  return transport.isOpen === false;
}

async function readDeviceDraft(transport: Transport): Promise<Draft> {
  const [config, steps] = await Promise.all([transport.getConfig(), transport.readMacro()]);
  return draftFromDevice(config, steps);
}

function readErrorMessage(error: unknown): string {
  return `Could not read the device configuration: ${error instanceof Error ? error.message : String(error)}`;
}

/** Shared device/workspace controller. Views own navigation, never another HID session. */
export function useWorkspace() {
  const hid = useHidDevice();
  const [draft, setDraft] = useState<Draft>(() => defaultDraft());
  const [deviceDraft, setDeviceDraft] = useState<Draft | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [pushState, setPushState] = useState<PushState>({ kind: 'idle' });
  const [saveToFlash, setSaveToFlash] = useState(true);
  /** Errors stay hidden until the first push attempt, so a fresh form is not red. */
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const connected = hid.phase === 'connected';

  const generationRef = useRef(0);
  const sessionRef = useRef<Session | null>(null);
  // A state-only lock leaves a same-tick double click free to upload twice.
  const pushLockRef = useRef<Session | null>(null);

  const isCurrent = useCallback((session: Session) => (
    sessionRef.current === session &&
    generationRef.current === session.generation &&
    sessionRef.current.transport === session.transport
  ), []);

  const loadSession = useCallback(async (session: Session) => {
    if (!isCurrent(session) || session.reading || pushLockRef.current === session) return;
    session.reading = true;
    setLoading(true);
    setLoadError(null);
    try {
      const next = await readDeviceDraft(session.transport);
      if (!isCurrent(session)) return;
      setDeviceDraft(next);
      if (session.adoptOnLoad) {
        setDraft(next);
        setProfileName(null);
        session.adoptOnLoad = false;
      }
      session.loaded = true;
    } catch (error) {
      if (!isCurrent(session)) return;
      session.loaded = false;
      setDeviceDraft(null);
      setLoadError(readErrorMessage(error));
    } finally {
      if (isCurrent(session)) {
        session.reading = false;
        setLoading(false);
      }
    }
  }, [isCurrent]);

  // Invalidate on commit, not a later passive effect: neither controls nor an old
  // promise may use the previous transport's baseline during a reconnect. Polls
  // do not reload the draft; only a new connected session does.
  useLayoutEffect(() => {
    const generation = ++generationRef.current;
    sessionRef.current = null;
    pushLockRef.current = null;
    setDeviceDraft(null);
    setProfileName(null);
    setPushState({ kind: 'idle' });
    setShowErrors(false);
    setLoadError(null);
    setLoading(false);

    if (!connected || !hid.transport) return;
    const session: Session = {
      generation, transport: hid.transport, loaded: false, reading: false, adoptOnLoad: true,
    };
    sessionRef.current = session;
    void loadSession(session);
    return () => {
      // Also cancels completions after unmount and StrictMode's discarded mount.
      if (sessionRef.current === session) {
        sessionRef.current = null;
        ++generationRef.current;
      }
    };
  }, [connected, hid.transport, loadSession]);

  const retryLoad = useCallback(() => {
    const session = sessionRef.current;
    if (session && !session.loaded) void loadSession(session);
  }, [loadSession]);

  const problems = useMemo(() => validateDraft(draft), [draft]);
  const dirty = deviceDraft === null || !draftMatchesDevice(draft, deviceDraft);

  const onPush = useCallback(async () => {
    const session = sessionRef.current;
    if (!connected || !session || session.transport !== hid.transport ||
        !isCurrent(session) || isTransportClosed(session.transport) ||
        !session.loaded || session.reading || pushLockRef.current === session) return;

    pushLockRef.current = session;
    setShowErrors(true);
    const transport = session.transport;
    try {
      const found = validateDraft(draft);
      if (found.length > 0) {
        setPushState({ kind: 'bad', message: `${found.length} problem(s) to fix first.` });
        return;
      }
      if (saveToFlash && hasButtonActions(draft) && !window.confirm(
        'Save automatic clicks/drags? On every power-up this macro starts after the boot delay and can activate items under the cursor. Stop from this page or unplug to interrupt. Replugging starts it again.',
      )) return;
      if (!isCurrent(session) || isTransportClosed(transport)) return;

      setPushState({ kind: 'busy', message: 'starting…' });
      const { saved } = await pushDraft(transport, draft, {
        save: saveToFlash,
        onProgress: (message) => {
          if (isCurrent(session)) setPushState({ kind: 'busy', message });
        },
      });
      if (!isCurrent(session)) return;

      const next = await readDeviceDraft(transport);
      if (!isCurrent(session)) return;
      setDeviceDraft(next);
      if (profileName) markPushed(profileName);
      setPushState({
        kind: 'ok',
        saved,
        message: saved
          ? 'Pushed and saved to flash. It will survive a replug.'
          : 'Pushed. Not saved — a replug restores the previous configuration.',
      });
      await hid.refresh();
    } catch (error) {
      if (!isCurrent(session)) return;
      setPushState({ kind: 'bad', message: error instanceof Error ? error.message : String(error) });
      // A partial push may have changed the device. If recovery also fails, keep
      // the read error separate and retryable; editing must not hide it.
      try {
        const next = await readDeviceDraft(transport);
        if (isCurrent(session)) setDeviceDraft(next);
      } catch (readError) {
        if (isCurrent(session)) {
          session.loaded = false;
          setDeviceDraft(null);
          setLoadError(readErrorMessage(readError));
        }
      }
    } finally {
      // An old push settling must not unlock a push in the replacement session.
      if (pushLockRef.current === session) pushLockRef.current = null;
    }
  }, [connected, draft, hid, isCurrent, profileName, saveToFlash]);

  const onResetToDevice = useCallback(() => {
    if (deviceDraft) setDraft(deviceDraft);
  }, [deviceDraft]);

  const onLoadProfile = useCallback((next: Draft, name: string) => {
    setDraft(next);
    setProfileName(name);
    setPushState({ kind: 'idle' });
  }, []);

  const deviceStopped = hid.status?.state === JigState.STOPPED || hid.status?.state === JigState.DONE;

  const ensureStopped = useCallback(async () => {
    const session = sessionRef.current;
    const message = 'Could not confirm the device is parked. Check the connection and try again.';
    if (!session || !isCurrent(session) || isTransportClosed(session.transport)) throw new Error(message);
    const status = await hid.stopJiggle();
    // Read the RETURN value, not hid.status: React still holds the previous poll.
    if (!isCurrent(session) || isTransportClosed(session.transport) || !status ||
        (status.state !== JigState.STOPPED && status.state !== JigState.DONE)) {
      throw new Error(message);
    }
  }, [hid, isCurrent]);

  const adoptMacro = useCallback((steps: JigStep[], durationMs?: number) => {
    // All authoring tools produce the existing macro schema; adopting never sends START.
    setDraft((d) => ({ ...d, kind: PatternKind.MACRO, steps, durationMs: durationMs ?? d.durationMs }));
    setProfileName(null);
    setPushState({ kind: 'idle' });
  }, []);

  return {
    hid, draft, setDraft, deviceDraft, profileName, setProfileName, pushState,
    saveToFlash, setSaveToFlash, showErrors, problems, dirty, connected, loading,
    loadError, retryLoad, onPush, onResetToDevice, onLoadProfile, deviceStopped,
    ensureStopped, adoptMacro,
  };
}

export type Workspace = ReturnType<typeof useWorkspace>;
